import React, { useState } from 'react';
import { Star } from 'lucide-react';

/**
 * StarRating — accessible 1..5 star rating component.
 * Props:
 *  - value: number (current rating, can be fractional for display)
 *  - onChange: (n) => void — if provided, becomes interactive
 *  - size: tailwind size class (e.g. "h-5 w-5")
 *  - showValue: boolean — show numeric value next to stars
 *  - count: number — show total count next to stars (e.g. "(12)")
 */
const StarRating = ({
  value = 0,
  onChange,
  size = 'h-5 w-5',
  showValue = false,
  count = null,
  testId = 'star-rating',
}) => {
  const interactive = typeof onChange === 'function';
  const [hover, setHover] = useState(0);

  const display = hover || value || 0;

  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <div className="inline-flex items-center" role={interactive ? 'radiogroup' : 'img'} aria-label={`Rating ${value} out of 5`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= Math.round(display);
          const half = !filled && n - 0.5 <= display;
          return (
            <button
              key={n}
              type="button"
              disabled={!interactive}
              onClick={() => interactive && onChange(n)}
              onMouseEnter={() => interactive && setHover(n)}
              onMouseLeave={() => interactive && setHover(0)}
              className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} p-0 bg-transparent border-0`}
              data-testid={`${testId}-star-${n}`}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
            >
              <Star
                className={`${size} ${filled ? 'fill-yellow-400 text-yellow-400' : half ? 'fill-yellow-200 text-yellow-400' : 'fill-none text-gray-300'}`}
              />
            </button>
          );
        })}
      </div>
      {showValue && value > 0 && (
        <span className="text-sm font-semibold text-gray-700 ml-1">{Number(value).toFixed(1)}</span>
      )}
      {count !== null && count !== undefined && (
        <span className="text-sm text-gray-500 ml-1">({count})</span>
      )}
    </div>
  );
};

export default StarRating;
